import { Link as RouterLink } from "@tanstack/react-router";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Stack,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import heroImage from "../../images/hero.jpeg"; // adjust path if needed
import story1 from "../../images/DSC_0125.JPG";
import story2 from "../../images/DSC_0130.JPG";
import story3 from "../../images/DSC_0131.JPG";
import ass from "../../images/ass.jpeg";
import street from "../../images/street.jpeg";
import borderTop from "../../images/divider4.svg";
import { brown } from "@mui/material/colors";
type StoryItem = {
  dateLabel: string;
  title: string;
  text: string;
  imageAlt: string;
  imageSrc: any;
};

const STORY: StoryItem[] = [
  {
    dateLabel: "2020",
    title: "Once upon a time…",
    text: `

And by that, we mean 2020. Our story began with a swipe on Tinder — proof that sometimes technology really does know what it’s doing 🙂 during a very uncertain time. Toffe (Christopher) had just returned from Barcelona because of Covid, and what started as simple conversations slowly became something that felt safe and familiar. We tried, we paused, and in September 2021 we found our way back to each other, choosing to begin again just as May (Phuong) was preparing to move to Lappeenranta for her studies.
    `,
    imageAlt: "Photo from when we met",
    imageSrc: story1,
  },
  {
    dateLabel: "2023",
    title: "The adventure",
    text: `
    From the beginning, love asked us to be patient. Distance became part of our story: countless train rides between Lappeenranta and Helsinki, many late nights on the phone, and a simple promise to call each other every single day — even if just for a few minutes. When May went on exchange to Belgium, the distance grew even more, but so did our commitment, with visits that reminded us that love doesn’t disappear with space; it grows with effort.
    `,
    imageAlt: "Photo from a trip",
    imageSrc: story2,
  },
  {
    dateLabel: "2025",
    title: "The proposal",
    text: `
    Five years later, our love is nowhere near perfect, but it is steady, honest, and deeply chosen. We are still learning about each other, still growing, and most importantly, still holding hands through life’s small and big moments. We don’t have all the answers yet, but we know we want to walk forward together, hand in hand, in the same direction. We are so grateful to celebrate this moment with all the family and friends who have been part of our journey 🤍
    `,
    imageAlt: "Photo from proposal day",
    imageSrc: story3,
  },
];

export function HomePage() {
  return (
    <Box sx={{ pb: 6 }}>
      {/* HERO */}
      <Box>
        <Box
          component="img"
          src={borderTop}
          alt="Vintage border"
          sx={{
            width: "100%",
            height: { xs: 60, md: 70 },
            display: "block",
            objectFit: "contain",
            opacity: 0.95,
          }}
        />
        <Typography
          variant="h2"
          sx={{
            fontWeight: 850,
            letterSpacing: -0.5,
            fontSize: { xs: 36, sm: 44, md: 56 },
            lineHeight: 1.05,
            textAlign: "center",
            fontFamily: "Quintessential",
            mb: "2rem",
          }}
        >
          We are getting married!
        </Typography>
      </Box>
      <Box
        sx={{
          position: "relative",
          minHeight: { xs: 360, md: 520, lg: 680 },
          // background:
          //   "linear-gradient(135deg, rgba(255,240,246,1), rgba(245,243,255,1), rgba(235,250,255,1))",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        {/* Background image layer (optional later) */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage: `url(${heroImage})`,
            backgroundSize: "cover",
            backgroundPosition: "top",
          }}
        />

        {/* Soft overlay to ensure text readability */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            // background:
            //   "radial-gradient(circle at 20% 10%, rgba(255,255,255,0.85), rgba(255,255,255,0.25) 55%, rgba(255,255,255,0.05))",
          }}
        />

        {/* QUICK INFO OVERLAY CARD */}
        <Container
          maxWidth="lg"
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: { xs: -86, md: -44 },
            zIndex: 1,
          }}
        >
          <Card
            sx={{
              mx: { xs: 2, sm: 0 },
              borderRadius: { xs: 3, md: 4 },
              border: "1px solid rgba(0,0,0,0.07)",
              bgcolor: "rgba(255,255,255,0.72)",
              backdropFilter: "blur(12px)",
              boxShadow: "0 12px 30px rgba(0,0,0,0.08)",
            }}
          >
            <CardContent sx={{ py: 2.2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid size={{ xs: 12, md: 4 }}>
                  <Typography variant="overline" color="text.secondary">
                    Date
                  </Typography>
                  <Typography fontWeight={800}>Saturday 4.7.2026</Typography>
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <Typography variant="overline" color="text.secondary">
                    Location
                  </Typography>
                  <Typography fontWeight={800}>Hämeenlinna, Finland</Typography>
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    justifyContent={{ md: "flex-end" }}
                  >
                    <Button
                      component={RouterLink}
                      to="/info"
                      variant="text"
                      sx={{ textTransform: "none", borderRadius: 999 }}
                    >
                      Practical info
                    </Button>
                    <Button
                      component={RouterLink}
                      to="/rsvp"
                      variant="contained"
                      sx={{
                        textTransform: "none",
                        borderRadius: 999,
                        boxShadow: "none",
                      }}
                    >
                      RSVP / Login
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Container>
      </Box>

      {/* Spacer because the overlay card hangs below hero */}
      <Box sx={{ height: { xs: 72, md: 64 } }} />

      {/* OUR STORY */}
      <Container maxWidth="lg" sx={{ mt: 6 }}>
        <Stack spacing={2} sx={{ mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 850 }}>
            Our story
          </Typography>
          <Typography color="text.secondary" sx={{ maxWidth: 720 }}>
            A small photo story — we’ll add more moments over time.
          </Typography>
        </Stack>

        <Stack spacing={3}>
          {STORY.map((item, idx) => {
            const imageLeft = idx % 2 === 0;

            return (
              <Card
                key={`${item.dateLabel}-${item.title}`}
                sx={{
                  borderRadius: { xs: 3, md: 4 },
                  border: "1px solid rgba(0,0,0,0.07)",
                  bgcolor: "rgba(255,255,255,0.75)",
                  backdropFilter: "blur(10px)",
                }}
              >
                <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                  <Grid
                    container
                    spacing={3}
                    alignItems="center"
                    direction={imageLeft ? "row" : "row-reverse"}
                  >
                    {/* Image */}
                    <Grid size={{ xs: 12, md: 5 }}>
                      <Box
                        sx={{
                          height: { xs: 220, sm: 260, md: 260 },
                          borderRadius: 3,
                          overflow: "hidden",
                          border: "1px solid rgba(0,0,0,0.06)",
                          backgroundImage: `url(${item.imageSrc})`,
                          backgroundSize: "cover",
                          backgroundPosition: "top",
                          position: "relative",
                        }}
                        aria-label={item.imageAlt}
                      >
                        <Box
                          sx={{
                            position: "absolute",
                            inset: 0,
                            opacity: 0.18,
                            background:
                              "radial-gradient(circle at 30% 20%, rgba(255,255,255,1), rgba(255,255,255,0) 55%)",
                          }}
                        />
                      </Box>
                    </Grid>

                    {/* Text */}
                    <Grid size={{ xs: 12, md: 7 }}>
                      <Stack spacing={1.2}>
                        <Chip
                          label={item.dateLabel}
                          size="small"
                          sx={{
                            width: "fit-content",
                            borderRadius: 999,
                            bgcolor: "rgba(255,255,255,0.7)",
                            border: "1px solid rgba(0,0,0,0.06)",
                            fontWeight: 700,
                          }}
                        />

                        <Typography variant="h5" sx={{ fontWeight: 850 }}>
                          {item.title}
                        </Typography>

                        <Typography
                          color="text.secondary"
                          sx={{ lineHeight: 1.7 }}
                        >
                          {item.text}
                        </Typography>
                      </Stack>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      </Container>
    </Box>
  );
}
